import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { fetchCategories, fetchCategoryLiveStats } from "../../api/categoryService";
import { API_BASE_URL } from "../../utils/constants";

const QUICK_PLAY_RECENT_KEY = "pixyquiz_recent_categories";
const TAB_OPTIONS = [
  { id: "all", label: "Toutes les catégories A-Z" },
];

function readRecentCategories() {
  try {
    return JSON.parse(localStorage.getItem(QUICK_PLAY_RECENT_KEY) || "[]");
  } catch (error) {
    return [];
  }
}

function getCategoryVisual(category) {
  return normalizeCategoryIconUrl(category?.icon_url || category?.icon || null);
}

function normalizeCategoryIconUrl(value) {
  if (!value) {
    return null;
  }

  const rawValue = String(value);
  const apiBase = API_BASE_URL.replace(/\/+$/, "");

  if (/^category\//i.test(rawValue)) {
    return `${apiBase}/media/${rawValue}`;
  }

  if (/^media\//i.test(rawValue)) {
    return `${apiBase}/${rawValue}`;
  }

  if (/^\/media\//i.test(rawValue)) {
    return `${apiBase}${rawValue}`;
  }

  try {
    const iconUrl = new URL(rawValue);
    const apiUrl = new URL(apiBase);

    if (
      iconUrl.pathname.startsWith("/media/") &&
      iconUrl.hostname === apiUrl.hostname &&
      iconUrl.port !== apiUrl.port
    ) {
      iconUrl.protocol = apiUrl.protocol;
      iconUrl.port = apiUrl.port;
    }

    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      iconUrl.protocol === "http:" &&
      iconUrl.hostname === apiUrl.hostname
    ) {
      iconUrl.protocol = "https:";
    }

    return iconUrl.toString();
  } catch (_error) {
    return rawValue;
  }
}

function formatCount(count, singular, plural) {
  const safeCount = Number(count || 0);
  return `${safeCount} ${safeCount > 1 ? plural : singular}`;
}

export function saveRecentQuickPlayCategory(category) {
  if (!category?.slug) {
    return;
  }

  const recentCategories = readRecentCategories();
  const nextRecent = [
    { slug: category.slug, name: category.name || category.slug },
    ...recentCategories.filter((item) => item?.slug !== category.slug),
  ].slice(0, 8);

  try {
    localStorage.setItem(QUICK_PLAY_RECENT_KEY, JSON.stringify(nextRecent));
  } catch (error) {}
}

export default function QuickPlayModal({
  isOpen,
  onClose,
  onSelectCategory,
}) {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [liveStats, setLiveStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingSlug, setPendingSlug] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError("");

    fetchCategories(controller.signal)
      .then((data) => {
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setError("Impossible de charger les categories pour le moment.");
      })
      .finally(() => {
        setLoading(false);
      });

    return () => controller.abort();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setLiveStats({});
      return undefined;
    }

    let isMounted = true;
    let controller = new AbortController();

    const loadLiveStats = () => {
      controller.abort();
      controller = new AbortController();

      fetchCategoryLiveStats(controller.signal)
        .then((data) => {
          if (!isMounted) {
            return;
          }

          const nextStats = {};
          if (Array.isArray(data)) {
            data.forEach((item) => {
              if (item?.category_slug) {
                nextStats[item.category_slug] = {
                  players_in_game: Number(item.players_in_game || 0),
                  friends_in_game: Number(item.friends_in_game || 0),
                };
              }
            });
          }
          setLiveStats(nextStats);
        })
        .catch(() => {
          if (isMounted) {
            setLiveStats({});
          }
        });
    };

    loadLiveStats();
    const intervalId = window.setInterval(loadLiveStats, 20000);

    return () => {
      isMounted = false;
      controller.abort();
      window.clearInterval(intervalId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setPendingSlug("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const recentCategories = useMemo(() => readRecentCategories(), [isOpen]);

  const decoratedCategories = useMemo(() => {
    const recentOrder = new Map(
      recentCategories.map((item, index) => [item.slug, index])
    );

    return categories.map((category, index) => {
      const popularitySeed = Number(category?.id || index + 1);
      const recentIndex = recentOrder.get(category.slug);

      return {
        ...category,
        visual: getCategoryVisual(category),
        liveStats: liveStats[category.slug] || {
          players_in_game: 0,
          friends_in_game: 0,
        },
        hasUploadedIcon: Boolean(category?.icon_url || category?.icon),
        recentIndex:
          typeof recentIndex === "number" ? recentIndex : Number.POSITIVE_INFINITY,
        popularityScore:
          (category?.play_count || 0) * 5 +
          ((popularitySeed * 17) % 13) +
          (recentIndex === 0 ? 20 : 0),
        playCount:
          category?.play_count ||
          Math.max(1, 40 - index * 2 + ((popularitySeed * 9) % 7)),
      };
    });
  }, [categories, liveStats, recentCategories]);

  const filteredCategories = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const searchedCategories = normalizedSearch
      ? decoratedCategories.filter((category) =>
          String(category?.name || "")
            .toLowerCase()
            .includes(normalizedSearch)
        )
      : decoratedCategories;

    const alphabeticalSort = (a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "fr", {
        sensitivity: "base",
      });

    if (activeTab === "recent") {
      return [...searchedCategories]
        .filter((category) => Number.isFinite(category.recentIndex))
        .sort(alphabeticalSort);
    }

    return [...searchedCategories].sort(alphabeticalSort);
  }, [activeTab, decoratedCategories, search]);

  if (!isOpen) {
    return null;
  }

  const handleSelect = async (category) => {
    if (!category?.slug || pendingSlug) {
      return;
    }

    setPendingSlug(category.slug);

    try {
      await onSelectCategory?.(category);
    } finally {
      setPendingSlug("");
    }
  };

  return createPortal(
    <>
      {/* Overlay — couche GPU isolée */}
      <div
        className="fixed inset-0 z-[140]"
        style={{ backgroundColor: "rgba(24,48,74,0.60)" }}
        onClick={onClose}
      />
      {/* Centering wrapper — z au-dessus de l'overlay */}
      <div
        className="fixed inset-0 z-[141] flex items-center justify-center p-4 sm:p-6"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="relative flex w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-slate-200 bg-white text-pixy-ink shadow-[0_32px_120px_rgba(47,157,226,0.24)] max-h-[90vh]"
          style={{ transform: "translateZ(0)" }}
          onClick={(event) => event.stopPropagation()}
        >
          <div className="border-b border-emerald-100 bg-[radial-gradient(circle_at_top_left,_rgba(95,181,111,0.18),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(47,157,226,0.16),_transparent_35%),linear-gradient(135deg,_#fffdf5,_#effcf6_58%,_#e9f7ff)] px-5 py-5 sm:px-7">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.32em] text-pixy-blue">
                  Quick Play
                </div>
                <h2 className="mt-2 text-3xl font-bold tracking-tight text-pixy-ink">
                  Lance une partie en quelques secondes
                </h2>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  Choisis une catégorie, filtre en direct, puis rejoins la room
                  sans casser le flow actuel du jeu.
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-pixy-blue/20 bg-white/80 px-4 py-2 text-sm font-semibold text-pixy-blue shadow-sm transition hover:bg-pixy-sky"
              >
                Fermer
              </button>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {TAB_OPTIONS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    activeTab === tab.id
                      ? "bg-gradient-to-r from-pixy-green to-pixy-blue text-white shadow-lg shadow-sky-400/20"
                      : "border border-white/80 bg-white/70 text-slate-600 hover:bg-pixy-sky hover:text-pixy-blue"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mt-4">
              <label className="sr-only" htmlFor="quick-play-search">
                Rechercher une catégorie
              </label>
              <input
                id="quick-play-search"
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher une catégorie..."
                className="w-full rounded-2xl border border-sky-100 bg-white/90 px-4 py-3 text-sm text-pixy-ink outline-none transition placeholder:text-slate-400 focus:border-pixy-blue focus:ring-2 focus:ring-pixy-blue/20"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-scroll bg-slate-50 px-5 py-5 sm:px-7" style={{ overscrollBehavior: "contain" }}>
            {loading ? (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-[28px] border border-sky-100 bg-white p-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 shrink-0 rounded-full bg-slate-200" />
                      <div className="flex-1 space-y-2.5">
                        <div className="h-4 w-3/4 rounded-full bg-slate-200" />
                        <div className="h-3 w-1/2 rounded-full bg-slate-200" />
                        <div className="h-3 w-1/2 rounded-full bg-slate-200" />
                        <div className="mt-1 h-3 w-1/3 rounded-full bg-slate-100" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="rounded-3xl border border-rose-200 bg-rose-50 px-6 py-10 text-center text-rose-700">
                {error}
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="rounded-3xl border border-sky-100 bg-white/80 px-6 py-10 text-center text-slate-600">
                Aucune catégorie ne correspond à cette recherche.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {filteredCategories.map((category) => {
                  const isPending = pendingSlug === category.slug;
                  const hasIcon = Boolean(category.hasUploadedIcon);

                  return (
                    <button
                      key={category.slug}
                      type="button"
                      onClick={() => handleSelect(category)}
                      disabled={Boolean(pendingSlug)}
                      className="rounded-[28px] border border-sky-100 bg-white text-left shadow-sm transition-colors duration-150 hover:border-pixy-blue/40 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <div className="flex items-center gap-4 p-4">
                        <div
                          className={`relative h-16 w-16 shrink-0 overflow-hidden shadow-sm ${
                            hasIcon
                              ? "quick-play-category-icon--image rounded-full border border-sky-100 bg-white p-2 ring-1 ring-white/80"
                              : "quick-play-category-icon--fallback rounded-2xl border border-white bg-gradient-to-br from-pixy-green via-pixy-blue to-sky-400"
                          }`}
                        >
                          {hasIcon ? (
                            <img
                              src={category.visual}
                              alt={category.name}
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-2xl font-black text-white">
                              {String(category?.name || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="truncate text-lg font-semibold text-pixy-ink">
                            {category.name}
                          </div>
                          <div className="mt-3 grid gap-1.5 text-sm text-slate-600">
                            <span>
                              👥{" "}
                              {formatCount(
                                category.liveStats.players_in_game,
                                "joueur en jeu",
                                "joueurs en jeu"
                              )}
                            </span>
                            <span>
                              🤝{" "}
                              {formatCount(
                                category.liveStats.friends_in_game,
                                "ami en jeu",
                                "amis en jeu"
                              )}
                            </span>
                          </div>
                          <div className="mt-3 text-xs font-semibold uppercase tracking-[0.22em] text-pixy-blue">
                            {isPending ? "Connexion a la room..." : "Jouer maintenant"}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
